/**
 * A card's identity — icon/glyph, name, cost, copy cap, description, and (for
 * pieces) its movement preview. `cardEntry` derives the display data from the
 * piece/ability registries; both the deck builder and the draft render from it,
 * so the two stay in sync. `CardFace` is a vertical tile used by the draft pool.
 */
import { Show } from "solid-js";
import {
  abilityDef,
  cardTier,
  isAbilityId,
  pieceDef,
  type CardTier,
  type CardTypeId,
  type PieceTypeId,
  type PlayerId,
} from "../../core";
import { PieceIcon } from "./PieceIcon";
import { MovePattern } from "./MovePattern";

/** Short, friendly label for a tier chip — descriptive, never a power ranking. */
const TIER_LABEL: Readonly<Record<CardTier, string>> = {
  staple: "Staple",
  role: "Role",
  bomb: "Bomb",
};

export interface CardEntry {
  readonly id: CardTypeId;
  readonly name: string;
  readonly cost: number;
  readonly desc: string;
  /** Max copies per deck, or undefined for no limit. */
  readonly max?: number;
  /** Set for pieces (rendered with piece art); abilities carry a `glyph`. */
  readonly pieceType?: PieceTypeId;
  readonly glyph?: string;
  /** Teaching tier (staple/role/bomb) — drives the chip and the pool-tile glow. */
  readonly tier: CardTier;
}

/** Normalise any card type into its display data, from the registries. */
export function cardEntry(id: CardTypeId): CardEntry {
  if (isAbilityId(id)) {
    const def = abilityDef(id);
    return { id, name: def.name, cost: def.cost, desc: def.blurb, glyph: def.glyph, tier: cardTier(id) };
  }
  const def = pieceDef(id);
  return {
    id,
    name: def.name,
    cost: def.cost,
    desc: def.description ?? `moves like a ${def.name.toLowerCase()}`,
    max: def.maxCopies,
    pieceType: def.id,
    tier: cardTier(id),
  };
}

/** A vertical card tile (icon, name, cost, description, move preview). */
export function CardFace(props: { id: CardTypeId; owner: PlayerId; showMove?: boolean }) {
  const entry = () => cardEntry(props.id);
  return (
    <div class="cardface" classList={{ [`tier-${entry().tier}`]: true }}>
      <div class="cardface-head">
        <Show
          when={entry().pieceType}
          fallback={<span class="glyph glyph-ability">{entry().glyph}</span>}
        >
          {(pt) => (
            <span class="glyph">
              <PieceIcon type={pt()} owner={props.owner} />
            </span>
          )}
        </Show>
        <div class="cardface-name">
          <div class="name">{entry().name}</div>
          <div class="cost">
            {entry().cost} {entry().cost === 1 ? "pt" : "pts"}
            {entry().max !== undefined ? ` · max ${entry().max}` : ""}
          </div>
        </div>
        <span class={`tier-chip tier-chip-${entry().tier}`}>{TIER_LABEL[entry().tier]}</span>
      </div>
      <div class="pdesc">{entry().desc}</div>
      <Show when={props.showMove !== false && entry().pieceType}>
        {(pt) => (
          <div class="cardface-move">
            <MovePattern type={pt()} owner={props.owner} />
          </div>
        )}
      </Show>
    </div>
  );
}
